import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, CheckCircle, BarChart3, Target, Zap, AlertTriangle, ChevronDown, ChevronUp, HelpCircle, Brain } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { signalsApi } from '@/services/api/signals';
import { jobsApi } from '@/services/api/jobs';
import { detectSignalDirections, extractSignalNames } from '@/utils/dsl-helpers';

interface IndividualSignalAnalysis {
  signalName: string;
  signalCount: number;
  direction: 'long' | 'short';
  maeStats: {
    average: number;
    median: number;
    std: number;
    min: number;
    max: number;
    percentiles: {
      25: number;
      50: number;
      75: number;
      90: number;
      95: number;
    };
    distribution: number[];
  };
  mfeStats: {
    average: number;
    median: number;
    std: number;
    min: number;
    max: number;
    percentiles: {
      25: number;
      50: number;
      75: number;
      90: number;
      95: number;
    };
    distribution: number[];
  };
  optimalConfig: {
    stopLoss: number;
    takeProfit: number;
    expectedWinRate: number;
    expectedReturn: number;
    expectedProfitFactor: number;
    expectedMaxDrawdown: number;
    confidenceScore: number;
  };
  metrics: {
    winRate: number;
    profitFactor: number;
    sharpeRatio: number;
    recoveryRatio: number;
    qualityScore: number;
  };
}

export interface SignalRiskAnalysis {
  metadata: {
    totalSignals: number;
    signalsAnalyzed: number;
    simulationBars: number;
    executionTimeMs: number;
    confidence: 'high' | 'medium' | 'low';
    warnings: string[];
  };
  aggregateStats: {
    maeStats: {
      average: number;
      median: number;
      std: number;
      min: number;
      max: number;
      percentiles: {
        25: number;
        50: number;
        75: number;
        90: number;
        95: number;
      };
      distribution: number[];
    };
    mfeStats: {
      average: number;
      median: number;
      std: number;
      min: number;
      max: number;
      percentiles: {
        25: number;
        50: number;
        75: number;
        90: number;
        95: number;
      };
      distribution: number[];
    };
  };
  signalAnalysis: IndividualSignalAnalysis[];
  ranking: {
    bestSignal: {
      name: string;
      reason: string;
      confidence: number;
      metrics: IndividualSignalAnalysis['metrics'];
    };
    signalRanking: Array<{
      rank: number;
      signalName: string;
      score: number;
      reason: string;
    }>;
  };
}

interface SignalRiskWidgetProps {
  dslCode: string;
  datasetId: string;
  scriptId?: string; // Optional: ID of saved script (for automatic detection)
  onAnalysisComplete?: (analysis: SignalRiskAnalysis) => void;
  onAnalysisError?: () => void;
  /** Callback when user clicks "Send to AI Assistant"; parent receives analysis and evaluation context. */
  onSendToAIAssistant?: (
    analysis: SignalRiskAnalysis,
    context: { evaluationMode: 'hold' | 'day_close' | 'session_close'; simulationBars?: number }
  ) => void;
}

const MAX_SIGNALS = 5;
const POLL_INTERVAL_MS = 2000;

export default function SignalRiskWidget({ dslCode, datasetId, scriptId, onAnalysisComplete, onAnalysisError, onSendToAIAssistant }: SignalRiskWidgetProps) {
  const { t } = useTranslation();
  const [analysis, setAnalysis] = useState<SignalRiskAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSignal, setSelectedSignal] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [evaluationMode, setEvaluationMode] = useState<'hold' | 'day_close' | 'session_close'>('hold');
  const [simulationBars, setSimulationBars] = useState<number>(20);
  const [selectedSignals, setSelectedSignals] = useState<string[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const availableSignals = useMemo(() => extractSignalNames(dslCode || ''), [dslCode]);

  const toggleSignal = (name: string) => {
    setSelectedSignals((prev) =>
      prev.includes(name)
        ? prev.filter((s) => s !== name)
        : prev.length < MAX_SIGNALS
          ? [...prev, name]
          : prev
    );
  };

  const startAnalysis = async () => {
    if ((!dslCode?.trim() && !scriptId) || !datasetId) return;
    if (selectedSignals.length < 1 || selectedSignals.length > MAX_SIGNALS) return;

    setIsLoading(true);
    setError(null);
    setJobId(null);

    try {
      const signalDirections = detectSignalDirections(dslCode || '');
      const request: any = {
        datasetId,
        signalNames: selectedSignals,
        analysis: {
          simulationMethod: 'realistic',
          simulationBars: evaluationMode === 'hold' ? simulationBars : 20,
          signalDirections,
          defaultDirection: 'long',
          evaluationMode,
        },
      };
      if (scriptId) request.scriptId = scriptId;
      else request.dslCode = dslCode;

      const { jobId: id } = await signalsApi.analyzeRisk(request);
      setJobId(id);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to start analysis');
      setIsLoading(false);
      onAnalysisError?.();
    }
  };

  useEffect(() => {
    if (!jobId) return;
    const poll = async () => {
      try {
        const job = await jobsApi.get(jobId);
        if (job.status === 'completed' && (job as { result?: SignalRiskAnalysis }).result) {
          const result = (job as { result: SignalRiskAnalysis }).result;
          setAnalysis(result);
          if (result.ranking?.bestSignal?.name) setSelectedSignal(result.ranking.bestSignal.name);
          onAnalysisComplete?.(result);
          setJobId(null);
          setIsLoading(false);
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } else if (job.status === 'failed') {
          setError((job as { error?: string }).error || 'Analysis failed');
          setAnalysis(null);
          setJobId(null);
          setIsLoading(false);
          onAnalysisError?.();
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch {
        // keep polling
      }
    };
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId]);

  // Helper function to format percentages
  const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`;

  // Compact view - shows summary and expand button
  const CompactView = () => {
    if (!analysis) return null;

    const bestSignal = analysis.ranking.bestSignal;
    const signalCount = analysis.metadata.signalsAnalyzed;

    return (
      <Card className="w-full cursor-pointer hover:shadow-md transition-all duration-200 border-dashed" onClick={() => setIsExpanded(true)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-gray-900">{t('signals.title')}</h3>
                <p className="text-xs text-muted-foreground">
                  {t('signals.signalsCount', { count: signalCount })} • {analysis.metadata.confidence} {t('signals.confidence').toLowerCase()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {bestSignal.name && (
                <div className="text-right mr-2">
                  <p className="text-xs text-muted-foreground">{t('signals.topSignal')}</p>
                  <p className="font-medium text-sm text-blue-600">{bestSignal.name}</p>
                </div>
              )}
              <div className="p-1 bg-gray-50 rounded-full">
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>

          {signalCount > 0 && bestSignal.name && (
            <div className="mt-3 flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-muted-foreground">
                  {formatPercent(bestSignal.metrics.winRate)} {t('signals.winRate')}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">•</div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">{t('signals.score')}:</span>
                <span className="text-xs font-medium">
                  {(() => {
                    const bestSignalData = analysis.signalAnalysis.find(s => s.signalName === bestSignal.name);
                    return bestSignalData && bestSignalData.signalCount > 0
                      ? `${(bestSignal.confidence / 10).toFixed(1)}/10.0`
                      : 'NA';
                  })()}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">•</div>
              <div className="text-xs text-muted-foreground">
                {t('signals.clickToExpand')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Show compact view when not expanded
  if (!isExpanded && analysis && !isLoading && !error) {
    return <CompactView />;
  }

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('signals.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show loading only when we have a job (user clicked Analyze); avoid showing when 0 signals selected
  if (isLoading && jobId) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('signals.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Skeleton className="h-4 w-4 rounded-full" />
            <p className="text-sm text-muted-foreground">
              {t('signals.analyzing')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state if analysis failed
  if (error) {
    return (
      <Card className="w-full border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('signals.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <div>
              <p className="text-sm font-medium text-destructive">
                {t('signals.riskAnalysisFailed')}
              </p>
              <p className="text-xs text-muted-foreground">
                {error}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Signal selection + Analyze when no analysis yet (no auto-run)
  if (!analysis && !isLoading) {
    const canAnalyze = (dslCode?.trim() || scriptId) && datasetId && selectedSignals.length >= 1 && selectedSignals.length <= MAX_SIGNALS;
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('signals.title')}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t('signals.setModeAndAnalyze')}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Evaluation mode + bars so they apply on next analysis */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="eval-mode-select" className="text-sm font-medium">{t('signals.evaluationMode')}</Label>
              <Select
                value={evaluationMode}
                onValueChange={(v: 'hold' | 'day_close' | 'session_close') => setEvaluationMode(v)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hold">{t('signals.holdBars')}</SelectItem>
                  <SelectItem value="day_close">{t('signals.dayClose')}</SelectItem>
                  <SelectItem value="session_close">{t('signals.sessionClose')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {evaluationMode === 'hold' && (
              <div className="flex items-center gap-2">
                <Label htmlFor="sim-bars-select" className="text-sm font-medium">{t('signals.holdBarsLabel')}</Label>
                <Select
                  value={simulationBars.toString()}
                  onValueChange={(v) => setSimulationBars(parseInt(v, 10))}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 25, 30, 40, 50, 60, 80, 100].map((n) => (
                      <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {availableSignals.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('signals.noSignalsInScript')} <code className="text-xs bg-muted px-1 rounded">{t('signals.noSignalsInScriptCode')}</code>{t('signals.noSignalsInScriptSuffix')}
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('signals.signalsSelectUpTo', { max: MAX_SIGNALS })}</Label>
                <div className="flex flex-wrap gap-3">
                  {availableSignals.map((name) => (
                    <label key={name} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedSignals.includes(name)}
                        onCheckedChange={() => toggleSignal(name)}
                        disabled={!selectedSignals.includes(name) && selectedSignals.length >= MAX_SIGNALS}
                      />
                      <span className="text-sm">{name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={startAnalysis} disabled={!canAnalyze}>
                {t('signals.analyze')}
              </Button>
            </>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!analysis) return null;

  const selectedSignalData = selectedSignal
    ? analysis.signalAnalysis.find(s => s.signalName === selectedSignal)
    : null;

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  const getQualityColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40';
    if (score >= 70) return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/40';
    if (score >= 50) return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/40';
    return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40';
  };

  return (
    <TooltipProvider>
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('signals.title')}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">
              {t('signals.signalsAnalyzed', { count: analysis.metadata.signalsAnalyzed })}
            </Badge>
            <Badge className={getConfidenceColor(analysis.metadata.confidence)}>
              {t('signals.confidence')} {analysis.metadata.confidence}
            </Badge>
            <Button
              variant="default"
              size="sm"
              onClick={() => { setAnalysis(null); setError(null); }}
              className="font-semibold shadow-sm ring-1 ring-primary/20"
            >
              {t('signals.reAnalyze')}
            </Button>
            {onSendToAIAssistant && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSendToAIAssistant(analysis, { evaluationMode, simulationBars: evaluationMode === 'hold' ? simulationBars : undefined })}
                className="gap-1.5"
              >
                <Brain className="w-3.5 h-3.5" />
                {t('signals.sendToAIAssistant')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(false)}
              className="h-6 w-6 p-0"
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
          </div>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {t('signals.changingModeHint')}
        </p>
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label htmlFor="evaluation-mode" className="text-sm font-medium">
              {t('signals.evaluationModeLabel')}
            </Label>
            <Select
              value={evaluationMode}
              onValueChange={(value: 'hold' | 'day_close' | 'session_close') => setEvaluationMode(value)}
            >
              <SelectTrigger id="evaluation-mode" className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hold">
                  <div className="flex flex-col">
                    <span>{t('signals.holdBars')}</span>
                    <span className="text-xs text-muted-foreground">{t('signals.holdDescription')}</span>
                  </div>
                </SelectItem>
                <SelectItem value="day_close">
                  <div className="flex flex-col">
                    <span>{t('signals.dayClose')}</span>
                    <span className="text-xs text-muted-foreground">{t('signals.dayCloseDescription')}</span>
                  </div>
                </SelectItem>
                <SelectItem value="session_close">
                  <div className="flex flex-col">
                    <span>{t('signals.sessionClose')}</span>
                    <span className="text-xs text-muted-foreground">{t('signals.sessionCloseDescription')}</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Show bars selector only when hold mode is selected */}
          {evaluationMode === 'hold' && (
            <div className="flex items-center gap-2">
              <Label htmlFor="simulation-bars" className="text-sm font-medium">
                {t('signals.holdBarsLabel2')}
              </Label>
              <Select
                value={simulationBars.toString()}
                onValueChange={(value) => setSimulationBars(parseInt(value, 10))}
              >
                <SelectTrigger id="simulation-bars" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 20 }, (_, i) => {
                    const value = (i + 1) * 5; // 5, 10, 15, ..., 100
                    return (
                      <SelectItem key={value} value={value.toString()}>
                        {t('signals.bars', { n: value })}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p className="text-sm">
                    {t('signals.barsTooltip')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
          
          {evaluationMode !== 'hold' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p className="text-sm">
                  {evaluationMode === 'day_close' ? t('signals.dayCloseTooltip') : t('signals.sessionCloseTooltip')}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Warnings */}
        {analysis.metadata.warnings.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1">
                {analysis.metadata.warnings.map((warning, i) => (
                  <li key={i} className="text-sm">{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Best Signal Highlight */}
        {analysis.ranking.bestSignal.name && (
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                <CheckCircle className="w-4 h-4" />
                {t('signals.bestSignal')} {analysis.ranking.bestSignal.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm text-green-600">{analysis.ranking.bestSignal.reason}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    {t('signals.score')}: 
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Quality Score of the best signal (0-10). Based on multiple risk and performance metrics.</p>
                      </TooltipContent>
                    </Tooltip>
                    <Badge className="bg-green-100 text-green-700">
                      {(() => {
                        const bestSignalData = analysis.signalAnalysis.find(s => s.signalName === analysis.ranking.bestSignal.name);
                        return bestSignalData && bestSignalData.signalCount > 0
                          ? `${(analysis.ranking.bestSignal.confidence / 10).toFixed(1)}/10.0`
                          : 'NA';
                      })()}
                    </Badge>
                  </span>
                  <span className="flex items-center gap-1">
                    {t('signals.winRateLabel')}: 
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Expected percentage of winning trades for this signal.</p>
                      </TooltipContent>
                    </Tooltip>
                    {formatPercent(analysis.ranking.bestSignal.metrics.winRate)}
                  </span>
                  <span className="flex items-center gap-1">
                    P/F: 
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Profit Factor: Ratio of total gains to total losses. &gt;1.0 is profitable.</p>
                      </TooltipContent>
                    </Tooltip>
                    {analysis.ranking.bestSignal.metrics.profitFactor.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signal Ranking */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              {t('signals.signalRanking')}
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Signal ranking ordered by Quality Score. Signals are evaluated based on Win Rate, Profit Factor, Recovery Ratio, and MAE. Click on a signal to see details.</p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analysis.ranking.signalRanking.map((signal) => (
                <div
                  key={signal.signalName}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedSignal === signal.signalName
                      ? 'bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800'
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => setSelectedSignal(signal.signalName)}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={signal.rank === 1 ? "default" : "outline"}>
                      #{signal.rank}
                    </Badge>
                    <span className="font-medium">{signal.signalName}</span>
                    {analysis.signalAnalysis.find(s => s.signalName === signal.signalName)?.direction && (
                      <Badge variant={analysis.signalAnalysis.find(s => s.signalName === signal.signalName)?.direction === 'long' ? 'default' : 'secondary'}>
                        {analysis.signalAnalysis.find(s => s.signalName === signal.signalName)?.direction === 'long' ? 'LONG' : 'SHORT'}
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      ({analysis.signalAnalysis.find(s => s.signalName === signal.signalName)?.signalCount || 0} signals)
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getQualityColor(signal.score)}>
                      {t('signals.score')}: {(() => {
                        const signalData = analysis.signalAnalysis.find(s => s.signalName === signal.signalName);
                        return signalData && signalData.signalCount > 0
                          ? `${(signal.score / 10).toFixed(1)}/10.0`
                          : 'NA';
                      })()}
                    </Badge>
                    <span className="text-sm text-muted-foreground">{signal.reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Individual Signal Details */}
        {selectedSignalData && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4" />
                {t('signals.detailsOf', { name: selectedSignalData.signalName })}
                <Badge variant={selectedSignalData.direction === 'long' ? 'default' : 'secondary'}>
                  {selectedSignalData.direction === 'long' ? 'LONG' : 'SHORT'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* MAE/MFE for this signal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium">MAE (Maximum Adverse Excursion)</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>MAE: Maximum price drop from entry point before recovery. Indicates the worst drawdown you would experience if you enter at this signal.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('signals.average')}</span>
                    <span className="font-semibold text-red-600">
                      {formatPercent(selectedSignalData.maeStats.average)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('signals.median')}</span>
                    <span className="font-semibold">{formatPercent(selectedSignalData.maeStats.median)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('signals.p95')}</span>
                    <span className="font-semibold">{formatPercent(selectedSignalData.maeStats.percentiles[95])}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium">MFE (Maximum Favorable Excursion)</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>MFE: Maximum price gain from entry point before retracing. Indicates the best potential profit you could have obtained.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('signals.average')}</span>
                    <span className="font-semibold text-green-600">
                      {formatPercent(selectedSignalData.mfeStats.average)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('signals.median')}</span>
                    <span className="font-semibold">{formatPercent(selectedSignalData.mfeStats.median)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{t('signals.p95')}</span>
                    <span className="font-semibold">{formatPercent(selectedSignalData.mfeStats.percentiles[95])}</span>
                  </div>
                </div>
              </div>

              {/* Signal Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    {t('signals.winRateLabel')}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Percentage of signals that would result in winning trades based on historical analysis.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="font-semibold text-green-600">
                    {formatPercent(selectedSignalData.metrics.winRate)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    Profit Factor
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Ratio of total gains to total losses. &gt;1.0 is profitable. Example: 2.0 means you win twice what you lose.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="font-semibold">{selectedSignalData.metrics.profitFactor.toFixed(2)}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    Recovery Ratio
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>MFE/MAE ratio. Indicates how much larger the potential profit is vs the drawdown. &gt;1.0 is favorable.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="font-semibold">{selectedSignalData.metrics.recoveryRatio.toFixed(2)}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    Quality Score
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Composite score (0-10) based on Win Rate, Profit Factor, Recovery Ratio, and MAE. Indicates the overall quality of the signal.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Badge className={getQualityColor(selectedSignalData.metrics.qualityScore)}>
                    {t('signals.score')}: {selectedSignalData.signalCount > 0
                      ? `${(selectedSignalData.metrics.qualityScore / 10).toFixed(1)}/10.0`
                      : 'NA'}
                  </Badge>
                </div>
              </div>

              {/* Optimal Config for this signal */}
              <div className="p-3 border rounded-lg">
                <div className="text-sm font-medium mb-2 flex items-center gap-2">
                  {t('signals.optimalConfiguration')}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Recommended Stop Loss and Take Profit configuration based on conservative MAE/MFE percentiles (P75 for SL, P50-P75 for TP). Uses composite scoring to avoid overfitting and penalizes extreme configurations for better generalization.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">SL:</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Stop Loss: Maximum loss percentage from entry price. If price drops by this %, position closes with a loss.</p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-semibold ml-1">
                      {formatPercent(selectedSignalData.optimalConfig.stopLoss)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">TP:</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Take Profit: Target gain percentage from entry price. If price rises by this %, position closes with a profit.</p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-semibold ml-1">
                      {formatPercent(selectedSignalData.optimalConfig.takeProfit)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">R:</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Risk/Reward Ratio: Relationship between potential gain (TP) and risk (SL). Example: R 2:1 means for every $1 risked, you expect to gain $2.</p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-semibold ml-1">
                      {(selectedSignalData.optimalConfig.takeProfit / selectedSignalData.optimalConfig.stopLoss).toFixed(2)}:1
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Win Rate:</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Win Rate: Percentage of trades that would reach Take Profit before Stop Loss with this configuration. Calculated through simulation.</p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-semibold ml-1 text-green-600">
                      {formatPercent(selectedSignalData.optimalConfig.expectedWinRate)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">{t('signals.confidence')}:</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Confidence: Composite score (0-100) based on Win Rate (40%), Profit Factor (30%), Risk/Reward (20%), and Drawdown (10%). Indicates overall scenario quality.</p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-semibold ml-1">
                      {selectedSignalData.optimalConfig.confidenceScore.toFixed(1)}/100
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Aggregate Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              {t('signals.aggregateStatistics')}
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Combined statistics from all analyzed signals. Provides an overview of risk and potential of all signals together.</p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  MAE Global
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Aggregate Maximum Adverse Excursion from all signals. Combined statistics of the worst observed drawdown.</p>
                    </TooltipContent>
                  </Tooltip>
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('signals.average')}:</span>
                    <span className="font-semibold text-red-600">
                      {formatPercent(analysis.aggregateStats.maeStats.average)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('signals.median')}:</span>
                    <span className="font-semibold">
                      {formatPercent(analysis.aggregateStats.maeStats.median)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('signals.p95')}:</span>
                    <span className="font-semibold">
                      {formatPercent(analysis.aggregateStats.maeStats.percentiles[95])}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  MFE Global
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Aggregate Maximum Favorable Excursion from all signals. Combined statistics of the best observed potential profit.</p>
                    </TooltipContent>
                  </Tooltip>
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>{t('signals.average')}:</span>
                    <span className="font-semibold text-green-600">
                      {formatPercent(analysis.aggregateStats.mfeStats.average)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('signals.median')}:</span>
                    <span className="font-semibold">
                      {formatPercent(analysis.aggregateStats.mfeStats.median)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('signals.p95')}:</span>
                    <span className="font-semibold">
                      {formatPercent(analysis.aggregateStats.mfeStats.percentiles[95])}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Summary */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t('signals.analysisCompletedIn', { ms: analysis.metadata.executionTimeMs })}
            </span>
          </div>
          <Badge variant="secondary">
            {t('signals.signalsProcessed', { count: analysis.metadata.signalsAnalyzed })}
          </Badge>
        </div>
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}